import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import Home from "./pages/Home";
import Courses from "./pages/Courses";
import CourseDetail from "./pages/CourseDetail";
import Player from "./pages/Player";
import MyCourses from "./pages/MyCourses";
import MyProfile from "./pages/MyProfile";
import ContaflixPro from "./pages/ContaflixPro";
import Auth from "./pages/Auth";
import AdminDashboard from "./pages/AdminDashboard";
import Templates from "./pages/Templates";
import ResetPassword from "./pages/ResetPassword";
import VerifyCertificate from "./pages/VerifyCertificate";
import PaymentResult from "./pages/PaymentResult";
import PromotionBanner from "./components/PromotionBanner";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setToken(token);
    setUser(userData);
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, API }}>
      <div className="App">
        <BrowserRouter>
          <PromotionBanner />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/cursos" element={<Courses />} />
            <Route path="/curso/:id" element={<CourseDetail />} />
            <Route
              path="/player/:courseId"
              element={user ? <Player /> : <Navigate to="/auth" />}
            />
            <Route
              path="/mis-cursos"
              element={user ? <MyCourses /> : <Navigate to="/auth" />}
            />
            <Route
              path="/mis-datos"
              element={user ? <MyProfile /> : <Navigate to="/auth" />}
            />
            <Route
              path="/plantillas"
              element={<Templates />}
            />
            <Route path="/contaflix-pro" element={<ContaflixPro />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verificar/:code" element={<VerifyCertificate />} />
            <Route path="/payment/result" element={<PaymentResult />} />
            <Route
              path="/admin"
              element={user && user.role === "admin" ? <AdminDashboard /> : <Navigate to="/" />}
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
